// src/components/panoramica/forecastEngine.js
// Motore previsionale di "Panoramica squadra".
//
// Dalla tabella voti (playerRatings: voto, fantavoto, gol, assist, gol subiti,
// cartellini… per giocatore e giornata) e dal calendario di Serie A ricostruisce
// i risultati reali di ogni partita giocata, stima la forza offensiva e
// difensiva di ogni squadra (modello di Poisson con fattore campo, pesato
// sulle partite più recenti e "ancorato" a una stima pre-stagione finché le
// partite sono poche) e da lì prevede, per ogni giocatore della rosa, la
// PROSSIMA partita:
//   - probabilità di giocare (presenze reali, probabili formazioni, infortuni);
//   - voto atteso (forma recente pesata + effetto dell'avversario, stimato
//     sull'intero campionato per ruolo);
//   - bonus attesi (quota del giocatore sui gol/assist della sua squadra ×
//     gol attesi contro quell'avversario), imbattibilità e gol subiti per il
//     portiere, cartellini;
//   - una forchetta (10°–90° percentile), non solo un numero.
// Tutto è deterministico e locale: nessun servizio esterno.
import { SERIE_A_CALENDAR, getTeamDifficulty, getUpcomingTeamFixtures } from '../../data/serieACalendar';

// ─── Regole di lega ─────────────────────────────────────────────────────────
export const DEFAULT_RULES = {
  goalBonus: 3,
  assistBonus: 1,
  cleanSheet: true, // +1 al portiere che non subisce gol
  cleanSheetBonus: 1,
  goalConcededMalus: 1, // portiere: −1 per gol subito
  yellowMalus: 0.5,
  redMalus: 1,
  ownGoalMalus: 2,
  // Stessi default di prima del rifacimento, per non cambiare le leghe già
  // configurate (le aste salvano solo le voci toccate).
  captain: false,
  viceCaptain: true,
  defenseModifier: true,
  defModBands: [
    { min: 6, bonus: 1 },
    { min: 6.5, bonus: 3 },
    { min: 7, bonus: 6 },
  ],
};

export function normalizeRules(raw = {}) {
  const r = { ...DEFAULT_RULES, ...(raw || {}) };
  // Compatibilità con le regole salvate dalla versione precedente
  // (ownGoal: boolean al posto del valore del malus).
  if (raw && raw.ownGoal === false && raw.ownGoalMalus === undefined) r.ownGoalMalus = 0;
  ['goalBonus', 'assistBonus', 'cleanSheetBonus', 'goalConcededMalus', 'yellowMalus', 'redMalus', 'ownGoalMalus'].forEach((k) => {
    const n = Number(r[k]);
    r[k] = Number.isFinite(n) ? n : DEFAULT_RULES[k];
  });
  if (!Array.isArray(r.defModBands) || !r.defModBands.length) r.defModBands = DEFAULT_RULES.defModBands;
  r.defModBands = r.defModBands
    .map((b) => ({ min: Number(b.min), bonus: Number(b.bonus) }))
    .filter((b) => Number.isFinite(b.min) && Number.isFinite(b.bonus))
    .sort((a, b) => a.min - b.min);
  if (!r.captain) r.viceCaptain = false;
  return r;
}

const ROLE_KEYS = ['P', 'D', 'C', 'A'];
const v = (x) => { const n = Number(x); return Number.isFinite(n) ? n : 0; };
const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
const round = (x, d = 2) => Math.round(x * 10 ** d) / 10 ** d;

function normalCdf(x) {
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989423 * Math.exp(-x * x / 2);
  const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  return x > 0 ? 1 - p : p;
}

// Ruolo "di reparto" P/D/C/A da un ruolo Classic o da token Mantra.
export function classicBucket(role = '') {
  const s = String(role || '').trim();
  const tokens = s.split(/[\s/;,]+/).filter(Boolean);
  const has = (list) => tokens.some((t) => list.includes(t));
  if (has(['P', 'Por', 'POR'])) return 'P';
  if (has(['D', 'Dd', 'Dc', 'Ds', 'B'])) return 'D';
  if (has(['C', 'E', 'M', 'T', 'W'])) return 'C';
  return 'A';
}

// ─── Indice dei voti ────────────────────────────────────────────────────────
// ratingsById: Map(playerId → { ratingsHistory: [{day, mark, fmark, team, …}] }).
// Ritorna le righe "giocate" (con voto) per giornata e per giocatore.
export function indexRatings(ratingsById) {
  const byDay = new Map();
  const byPlayer = new Map();
  let maxDay = 0;
  const entries = ratingsById && typeof ratingsById.forEach === 'function'
    ? ratingsById
    : new Map(Object.entries(ratingsById || {}));
  entries.forEach((doc, id) => {
    const history = Array.isArray(doc?.ratingsHistory) ? doc.ratingsHistory : [];
    maxDay = Math.max(maxDay, v(doc?.ratingsMaxDay), history.length);
    const rows = [];
    history.forEach((r) => {
      const day = v(r?.day);
      if (!day || !r?.team) return;
      const row = {
        id: String(id), day, team: r.team, role: r.role || '',
        mark: v(r.mark), fmark: v(r.fmark),
        goal: v(r.goal) + v(r.penalty), // "penalty" = rigori segnati, contati a parte
        assist: v(r.assist), gotgoal: v(r.gotgoal), owngoal: v(r.owngoal),
        ycard: v(r.ycard), rcard: v(r.rcard), enter: Boolean(r.enter), exit: Boolean(r.exit),
        spenalty: v(r.spenalty),
      };
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push(row);
      if (row.mark > 0) rows.push(row);
    });
    rows.sort((a, b) => a.day - b.day);
    byPlayer.set(String(id), rows);
  });
  return { byDay, byPlayer, maxDay };
}

// ─── Risultati delle partite giocate ────────────────────────────────────────
// Gol di una squadra = gol (+ rigori) dei suoi giocatori + autogol avversari.
// Verificato sui dati reali: coincide coi gol subiti dal portiere avversario.
export function buildMatchResults({ byDay, maxDay, untilDay = Infinity, calendar = SERIE_A_CALENDAR }) {
  const matches = [];
  calendar.forEach(({ giornata, matches: pairs }) => {
    if (giornata > maxDay || giornata >= untilDay) return;
    const rows = byDay.get(giornata) || [];
    if (!rows.length) return;
    const byTeam = new Map();
    rows.forEach((r) => { if (!byTeam.has(r.team)) byTeam.set(r.team, []); byTeam.get(r.team).push(r); });
    pairs.forEach(([home, away]) => {
      const H = byTeam.get(home);
      const A = byTeam.get(away);
      if (!H?.length || !A?.length) return; // rinviata o dati mancanti
      const scored = (T, O) => T.reduce((s, r) => s + r.goal, 0) + O.reduce((s, r) => s + r.owngoal, 0);
      matches.push({ day: giornata, home, away, hg: scored(H, A), ag: scored(A, H) });
    });
  });
  return matches;
}

// ─── Forza delle squadre (Poisson con fattore campo) ────────────────────────
// λ(casa) = μ·h·att[casa]·def[ospite], λ(ospite) = μ·att[ospite]·def[casa]
// (def > 1 = difesa che concede più della media). Stima iterativa con:
//  - peso decrescente per le partite più vecchie (forma recente);
//  - "partite virtuali" a priori dalla difficoltà pre-stagione, che contano
//    molto a inizio campionato e sempre meno man mano che si gioca.
// Peso della stima pre-stagione, in "partite virtuali". Scelto col backtest
// sulle giornate già giocate (3, 5, 8, 12 provati: 8 dà la miglior
// verosimiglianza sui gol e niente λ esagerati con poche partite).
const PRIOR_GAMES = 8;
const DECAY = 0.9; // peso di una partita giocata k giornate fa: 0.9^k

function priorFromDifficulty(team) {
  const d = getTeamDifficulty(team); // 1 (debole) … 5 (big)
  const t = (d - 3) / 2; // −1 … +1
  return { att: Math.exp(0.28 * t), def: Math.exp(-0.25 * t) };
}

export function fitTeamStrength(matches, { teams = null, currentDay = null, priorGames = PRIOR_GAMES } = {}) {
  const allTeams = new Set(teams || []);
  matches.forEach((m) => { allTeams.add(m.home); allTeams.add(m.away); });
  const list = [...allTeams];
  const lastDay = currentDay ?? Math.max(0, ...matches.map((m) => m.day));
  const w = (m) => DECAY ** Math.max(0, lastDay - m.day);

  const totW = matches.reduce((s, m) => s + w(m), 0);
  const hg = matches.reduce((s, m) => s + w(m) * m.hg, 0);
  const ag = matches.reduce((s, m) => s + w(m) * m.ag, 0);
  // Media gol per squadra a partita e vantaggio casa, entrambi "tirati" verso
  // valori tipici della Serie A (≈1.35 gol, casa ≈ +15%) con poche partite.
  const mu = totW ? ((hg + ag) / (2 * totW) * totW + 1.35 * 10) / (totW + 10) : 1.35;
  const homeRaw = ag > 0 ? hg / ag : 1.15;
  const home = clamp((homeRaw * totW + 1.15 * 20) / (totW + 20), 1, 1.4);

  const att = new Map();
  const def = new Map();
  const prior = new Map();
  list.forEach((t) => { const p = priorFromDifficulty(t); prior.set(t, p); att.set(t, p.att); def.set(t, p.def); });

  for (let iter = 0; iter < 40; iter++) {
    const numA = new Map(); const denA = new Map(); const numD = new Map(); const denD = new Map();
    list.forEach((t) => { numA.set(t, 0); denA.set(t, 0); numD.set(t, 0); denD.set(t, 0); });
    matches.forEach((m) => {
      const wt = w(m);
      numA.set(m.home, numA.get(m.home) + wt * m.hg);
      denA.set(m.home, denA.get(m.home) + wt * mu * home * def.get(m.away));
      numA.set(m.away, numA.get(m.away) + wt * m.ag);
      denA.set(m.away, denA.get(m.away) + wt * mu * def.get(m.home));
      numD.set(m.away, numD.get(m.away) + wt * m.hg);
      denD.set(m.away, denD.get(m.away) + wt * mu * home * att.get(m.home));
      numD.set(m.home, numD.get(m.home) + wt * m.ag);
      denD.set(m.home, denD.get(m.home) + wt * mu * att.get(m.away));
    });
    list.forEach((t) => {
      const p = prior.get(t);
      att.set(t, (numA.get(t) + priorGames * p.att * mu) / (denA.get(t) + priorGames * mu));
      def.set(t, (numD.get(t) + priorGames * p.def * mu) / (denD.get(t) + priorGames * mu));
    });
    // Normalizza: media geometrica 1 per attacco e difesa.
    const gm = (map) => Math.exp(list.reduce((s, t) => s + Math.log(map.get(t)), 0) / list.length);
    const ga = gm(att); const gd = gm(def);
    list.forEach((t) => { att.set(t, att.get(t) / ga); def.set(t, def.get(t) / gd); });
  }

  return { mu, home, att, def, games: matches.length, lastDay };
}

export function expectedGoals(strength, team, opponent, isHome) {
  const a = strength.att.get(team) ?? 1;
  const d = strength.def.get(opponent) ?? 1;
  const raw = strength.mu * a * d * (isHome ? strength.home : 1);
  // Smorza i valori estremi (oltre 2.5 gol attesi conta metà dell'eccesso):
  // con poche giornate una goleada pesa troppo sulla stima.
  return raw > 2.5 ? 2.5 + (raw - 2.5) * 0.5 : raw;
}

// ─── Effetto avversario sul VOTO, stimato su tutto il campionato ────────────
// Per ogni ruolo: di quanto si sposta il voto (rispetto alla media del
// giocatore) per ogni unità di log-forza dell'avversario (difesa avversaria
// per chi attacca, attacco avversario per chi difende) e giocando in casa.
// Regressione ai minimi quadrati, con coefficienti tenuti in un intervallo
// ragionevole (pochi dati a inizio stagione).
export function fitVoteEffects({ byPlayer, matchesByTeamDay, strength }) {
  const acc = {};
  ROLE_KEYS.forEach((r) => { acc[r] = { sxx: 0, sxy: 0, n: 0, home: 0, away: 0, nh: 0, na: 0 }; });
  byPlayer.forEach((rows) => {
    if (rows.length < 2) return;
    const mean = rows.reduce((s, r) => s + r.mark, 0) / rows.length;
    rows.forEach((r) => {
      const m = matchesByTeamDay.get(`${r.team}|${r.day}`);
      if (!m) return;
      const role = classicBucket(r.role);
      const x = role === 'P' || role === 'D'
        ? Math.log(strength.att.get(m.opponent) ?? 1)
        : -Math.log(strength.def.get(m.opponent) ?? 1);
      const y = r.mark - mean;
      const a = acc[role];
      a.sxx += x * x; a.sxy += x * y; a.n += 1;
      if (m.isHome) { a.home += y; a.nh += 1; } else { a.away += y; a.na += 1; }
    });
  });
  const out = {};
  ROLE_KEYS.forEach((r) => {
    const a = acc[r];
    // Segno atteso: avversario più forte → voto più basso (slope negativo).
    const slope = a.sxx > 0 ? a.sxy / (a.sxx + 2) : 0;
    const homeEff = a.nh && a.na ? (a.home / a.nh - a.away / a.na) / 2 : 0.05;
    out[r] = { slope: clamp(slope, -1.2, 0), home: clamp(homeEff, 0, 0.25) };
  });
  return out;
}

// ─── Statistiche di ruolo (per tirare verso la media chi ha pochi dati) ─────
function buildRoleBaselines(byPlayer, roleOf) {
  const sums = {};
  ROLE_KEYS.forEach((r) => { sums[r] = { mark: 0, n: 0, sd2: 0, ns: 0 }; });
  byPlayer.forEach((rows, id) => {
    if (!rows.length) return;
    const role = roleOf(id, rows);
    const s = sums[role];
    const mean = rows.reduce((a, r) => a + r.mark, 0) / rows.length;
    rows.forEach((r) => { s.mark += r.mark; s.n += 1; });
    if (rows.length >= 3) {
      const fmean = rows.reduce((a, r) => a + r.fmark, 0) / rows.length;
      s.sd2 += rows.reduce((a, r) => a + (r.fmark - fmean) ** 2, 0) / (rows.length - 1);
      s.ns += 1;
    }
    return mean;
  });
  const out = {};
  ROLE_KEYS.forEach((r) => {
    const s = sums[r];
    out[r] = { mark: s.n ? s.mark / s.n : 6, sd: s.ns ? Math.sqrt(s.sd2 / s.ns) : 1.2 };
  });
  return out;
}

// ─── Contesto di campionato (si calcola una volta per pagina) ───────────────
export function buildLeagueContext({ ratingsById, untilDay = Infinity, now = Date.now(), priorGames = PRIOR_GAMES }) {
  const { byDay, byPlayer, maxDay } = indexRatings(ratingsById);
  // Backtest: si possono "nascondere" le giornate da untilDay in poi.
  let players = byPlayer;
  if (Number.isFinite(untilDay)) {
    players = new Map();
    byPlayer.forEach((rows, id) => players.set(id, rows.filter((r) => r.day < untilDay)));
  }
  const matches = buildMatchResults({ byDay, maxDay, untilDay });
  const strength = fitTeamStrength(matches, { teams: SERIE_A_CALENDAR[0]?.matches.flat(), priorGames });

  const matchesByTeamDay = new Map();
  const teamMatches = new Map(); // team → [{day, gf, ga, opponent, isHome}]
  matches.forEach((m) => {
    matchesByTeamDay.set(`${m.home}|${m.day}`, { opponent: m.away, isHome: true, gf: m.hg, ga: m.ag });
    matchesByTeamDay.set(`${m.away}|${m.day}`, { opponent: m.home, isHome: false, gf: m.ag, ga: m.hg });
    [[m.home, m.hg, m.ag, m.away, true], [m.away, m.ag, m.hg, m.home, false]].forEach(([t, gf, ga, o, h]) => {
      if (!teamMatches.has(t)) teamMatches.set(t, []);
      teamMatches.get(t).push({ day: m.day, gf, ga, opponent: o, isHome: h });
    });
  });

  const roleCache = new Map();
  const roleOf = (id, rows) => {
    if (roleCache.has(id)) return roleCache.get(id);
    const r = classicBucket(rows?.[rows.length - 1]?.role || '');
    roleCache.set(id, r);
    return r;
  };
  const voteEffects = fitVoteEffects({ byPlayer: players, matchesByTeamDay, strength });
  const roleBaselines = buildRoleBaselines(players, roleOf);

  return {
    byPlayer: players, matches, strength, matchesByTeamDay, teamMatches,
    voteEffects, roleBaselines, maxDay: Number.isFinite(untilDay) ? untilDay - 1 : maxDay, now,
  };
}

// ─── Previsione di un giocatore per una partita ─────────────────────────────
// fixture: { opponent, home, giornata, date } (dal calendario).
// availability: { injury?, lineup?: {status, prob} } (stessa forma di
// buildPlayerAvailability) — opzionale.
const ROLE_GOAL_SHARE_PRIOR = { P: 0, D: 0.035, C: 0.075, A: 0.2 };
const ROLE_ASSIST_SHARE_PRIOR = { P: 0.005, D: 0.04, C: 0.08, A: 0.08 };
const HALF_LIFE = 3; // partite: peso dimezzato ogni 3 partite indietro

export function forecastPlayer({ playerId, role: rawRole, team, fixture, ctx, availability = null, manualOut = false, rules = DEFAULT_RULES }) {
  const role = classicBucket(rawRole);
  const rows = ctx.byPlayer.get(String(playerId)) || [];
  const base = ctx.roleBaselines[role] || { mark: 6, sd: 1.2 };
  const teamGames = (ctx.teamMatches.get(team) || []);

  // 1) Probabilità di giocare (prendere voto).
  const firstDay = rows[0]?.day ?? Infinity;
  const gamesSinceFirst = teamGames.filter((g) => g.day >= Math.min(firstDay, ctx.maxDay)).length || teamGames.length;
  const apps = rows.length;
  const starts = rows.filter((r) => !r.enter).length;
  const appRate = (apps + 0.6 * 2) / (Math.max(gamesSinceFirst, apps) + 2); // prior 60% su 2 partite
  let pPlay = appRate;
  const reasons = [];
  const lineup = availability?.lineup;
  if (lineup && Number.isFinite(Number(lineup.prob))) {
    // Le probabili formazioni (titolare ~90, panchina ~30) pesano più dello storico.
    const lp = clamp(Number(lineup.prob) / 100, 0.02, 0.98);
    const asPlay = lineup.status === 'starter' ? 0.25 + 0.75 * lp : 0.15 + 0.6 * lp;
    pPlay = 0.35 * appRate + 0.65 * clamp(asPlay, 0.05, 0.97);
  }
  if (availability?.injury || manualOut) {
    pPlay = 0;
    reasons.push({ key: 'injury', text: manualOut ? 'Segnalato da te come indisponibile' : 'Infortunato' });
  }
  if (!fixture) pPlay = 0;

  // 2) Voto atteso: media pesata sulle partite recenti, tirata verso la media
  //    di ruolo quando i dati sono pochi.
  let wSum = 0; let wMark = 0;
  rows.forEach((r, i) => {
    const age = rows.length - 1 - i;
    const w = 0.5 ** (age / HALF_LIFE);
    wSum += w; wMark += w * r.mark;
  });
  const SHRINK = 2.5;
  const recentMark = wSum ? wMark / wSum : base.mark;
  const seasonMark = apps ? rows.reduce((s, r) => s + r.mark, 0) / apps : base.mark;
  const markBase = (recentMark * wSum + base.mark * SHRINK) / (wSum + SHRINK);

  // Effetto avversario e campo (stimati sul campionato, per ruolo).
  const eff = ctx.voteEffects[role] || { slope: 0, home: 0 };
  let oppEffect = 0; let homeEffect = 0; let lambdaFor = ctx.strength.mu; let lambdaAgainst = ctx.strength.mu;
  if (fixture) {
    const opp = fixture.opponent;
    const x = role === 'P' || role === 'D'
      ? Math.log(ctx.strength.att.get(opp) ?? 1)
      : -Math.log(ctx.strength.def.get(opp) ?? 1);
    oppEffect = eff.slope * x;
    homeEffect = fixture.home ? eff.home : -eff.home;
    lambdaFor = expectedGoals(ctx.strength, team, opp, fixture.home);
    lambdaAgainst = expectedGoals(ctx.strength, opp, team, !fixture.home);
  }
  const expMark = markBase + oppEffect + homeEffect;

  // 3) Bonus: quota del giocatore sui gol/assist della squadra, nelle partite
  //    in cui ha giocato, × gol attesi della squadra in questa partita.
  const playedDays = new Set(rows.map((r) => r.day));
  const teamGoalsWhenPlayed = teamGames.filter((g) => playedDays.has(g.day)).reduce((s, g) => s + g.gf, 0);
  const goals = rows.reduce((s, r) => s + r.goal, 0);
  const assists = rows.reduce((s, r) => s + r.assist, 0);
  const startShare = apps ? starts / apps : 0.7; // chi entra dalla panchina gioca meno minuti
  const minutesFactor = 0.45 + 0.55 * startShare;
  const PRIOR_TEAM_GOALS = 6;
  const goalShare = (goals + ROLE_GOAL_SHARE_PRIOR[role] * PRIOR_TEAM_GOALS) / (teamGoalsWhenPlayed + PRIOR_TEAM_GOALS);
  const assistShare = (assists + ROLE_ASSIST_SHARE_PRIOR[role] * PRIOR_TEAM_GOALS) / (teamGoalsWhenPlayed + PRIOR_TEAM_GOALS);
  const expGoals = role === 'P' ? 0 : lambdaFor * goalShare * minutesFactor;
  const expAssists = lambdaFor * assistShare * minutesFactor;
  const pGoal = 1 - Math.exp(-expGoals);
  const pAssist = 1 - Math.exp(-expAssists);

  // Portiere: gol subiti e imbattibilità.
  const pCleanSheet = Math.exp(-lambdaAgainst);
  let gkBonus = 0;
  if (role === 'P') {
    gkBonus -= rules.goalConcededMalus * lambdaAgainst;
    if (rules.cleanSheet) gkBonus += rules.cleanSheetBonus * pCleanSheet;
  }
  const cardRate = apps ? rows.reduce((s, r) => s + r.ycard, 0) / (apps + 2) : 0.15;
  const redRate = apps ? rows.reduce((s, r) => s + r.rcard, 0) / (apps + 6) : 0.01;
  const ownRate = apps ? rows.reduce((s, r) => s + r.owngoal, 0) / (apps + 10) : 0.005;
  const malus = rules.yellowMalus * cardRate + rules.redMalus * redRate + rules.ownGoalMalus * ownRate;

  const bonus = rules.goalBonus * expGoals + rules.assistBonus * expAssists + gkBonus - malus;
  const expFV = expMark + bonus;

  // 4) Incertezza: dispersione dei fantavoti del giocatore, tirata verso
  //    quella tipica del ruolo.
  let sd = base.sd;
  if (apps >= 3) {
    const fmean = rows.reduce((s, r) => s + r.fmark, 0) / apps;
    const own = Math.sqrt(rows.reduce((s, r) => s + (r.fmark - fmean) ** 2, 0) / (apps - 1));
    sd = Math.sqrt((own ** 2 * apps + base.sd ** 2 * 4) / (apps + 4));
  }

  // 5) Fattori "spiegabili" (in punti di fantavoto rispetto a un giocatore
  //    medio del ruolo).
  const formDelta = recentMark - seasonMark;
  const factors = [
    { key: 'base', label: 'Rendimento', value: markBase - base.mark },
    { key: 'form', label: 'Forma recente', value: clamp(formDelta, -2, 2) * (wSum / (wSum + SHRINK)) },
    { key: 'opponent', label: 'Avversario', value: oppEffect },
    { key: 'venue', label: fixture?.home ? 'In casa' : 'In trasferta', value: homeEffect },
    { key: 'bonus', label: 'Bonus attesi', value: bonus },
  ];

  const last = rows.slice(-5).map((r) => ({ day: r.day, fmark: r.fmark, mark: r.mark }));

  return {
    playerId: String(playerId), role, team, fixture,
    pPlay: round(pPlay, 3), expMark: round(expMark), expFV: round(expFV), bonus: round(bonus),
    expGoals: round(expGoals, 3), expAssists: round(expAssists, 3), pGoal: round(pGoal, 3), pAssist: round(pAssist, 3),
    pCleanSheet: round(pCleanSheet, 3), lambdaFor: round(lambdaFor), lambdaAgainst: round(lambdaAgainst),
    sd: round(sd), p10: round(expFV - 1.2816 * sd), p90: round(expFV + 1.2816 * sd),
    // Valore "atteso" contando anche il rischio di non prendere voto.
    value: round(pPlay * expFV),
    apps, starts, seasonMark: round(seasonMark), recentMark: round(recentMark), formDelta: round(formDelta),
    factors, last, reasons,
  };
}

// Colore della previsione rispetto al ruolo: il portiere ha fantavoti più
// bassi per natura (malus gol subiti), un 5 per lui è nella norma.
export function fvTone(f) {
  if (!f || f.pPlay === 0) return '';
  const [low, high] = f.role === 'P' ? [4.4, 6] : [5.8, 7];
  return f.expFV >= high ? 'is-high' : f.expFV < low ? 'is-low' : '';
}

// Prossime partite di un giocatore (per il riquadro "prossima partita").
export function forecastNextFixtures({ playerId, role, team, ctx, availability, rules, count = 1, now = Date.now() }) {
  return getUpcomingTeamFixtures(team, count, now).map((fixture) => forecastPlayer({
    playerId, role, team, fixture, ctx, availability, rules,
  }));
}

// ─── Modificatore di difesa atteso ──────────────────────────────────────────
// Media dei voti puri di portiere + 3 migliori difensori (servono almeno 4
// difensori), con la media che oscilla attorno al valore atteso.
export function expectedDefenseModifier(goalkeeper, defenders, rules) {
  if (!rules.defenseModifier) return { enabled: false, applies: false, expected: 0 };
  if (!goalkeeper || defenders.length < 4) {
    return { enabled: true, applies: false, expected: 0, reason: defenders.length < 4 ? `servono almeno 4 difensori (ne schieri ${defenders.length})` : 'manca il portiere' };
  }
  const best3 = [...defenders].sort((a, b) => b.expMark - a.expMark).slice(0, 3);
  const avg = (goalkeeper.expMark + best3.reduce((s, d) => s + d.expMark, 0)) / 4;
  const sigma = 0.38;
  let expected = 0;
  const bands = [...rules.defModBands].sort((a, b) => b.min - a.min);
  let prev = 0;
  bands.forEach((b) => {
    const p = 1 - normalCdf((b.min - avg) / sigma);
    expected += (p - prev) * b.bonus;
    prev = p;
  });
  // Il modificatore vale solo se i 4 prendono voto: si pesa per quella probabilità.
  const pAll = [goalkeeper, ...best3].reduce((s, x) => s * Math.max(0.05, x.pPlay), 1);
  return { enabled: true, applies: true, average: round(avg), expected: round(expected * (0.5 + 0.5 * pAll)) };
}
